import { FC } from 'react';
import { useState, useCallback } from 'react';
import { FolderOpen, FileText, ExternalLink } from 'lucide-react';
import { EditableDataTable, EditableColumn } from '@/components/ui';
import { TopicRow } from '../types';
import { ProposedTopicRow } from '../types';
import { RelevanceScoreDisplay } from './RelevanceScoreDisplay';

interface TopicsTabProps {
  description: string;
  rows: ProposedTopicRow[];
  loading?: boolean;
  error?: string | null;
  onViewDetails?: (topic: TopicRow) => void;
  onViewPosts?: (topicId: number) => void;
  onUpdate?: (topicId: number, updates: Partial<TopicRow>) => Promise<void>;
  pagination?: {
    pageIndex: number;
    pageSize: number;
    totalCount: number;
  };
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  onRefetch?: (sortBy?: string, sortOrder?: 'ASC' | 'DESC', category?: string) => Promise<void>;
  onGlobalSearch?: (query: string) => Promise<void>;
  globalFilterValue?: string;
}

/**
 * Proposed Topics Tab Component
 * Shows suggested topics with categories from IDM
 * Uses EditableDataTable with pagination support
 */
export const TopicsTab: FC<TopicsTabProps> = ({
  description,
  rows,
  loading = false,
  error = null,
  pagination,
  onPaginationChange,
  onRefetch,
  onGlobalSearch,
  globalFilterValue,
}) => {
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());

  const MAX_TEXT_LENGTH = 300;

  const toggleTopicExpansion = useCallback((topicKey: string) => {
    setExpandedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topicKey)) {
        next.delete(topicKey);
      } else {
        next.add(topicKey);
      }
      return next;
    });
  }, []);

  const mapColumnKeyToBackendField = useCallback((columnKey: string): string => {
    const mapping: Record<string, string> = {
      'category': 'category',
      'topics': 'topicCount',
    };
    return mapping[columnKey] || columnKey;
  }, []);

  const handleSortingChange = useCallback((newSorting: Array<{ id: string; desc: boolean }>) => {
    setSorting(newSorting);
    if (onRefetch && newSorting.length > 0) {
      const sortItem = newSorting[0];
      const sortBy = mapColumnKeyToBackendField(sortItem.id);
      const sortOrder = sortItem.desc ? 'DESC' : 'ASC';
      onRefetch(sortBy as 'category' | 'topicCount' | 'createdAt', sortOrder);
    } else if (onRefetch) {
      // Reset to default sorting
      onRefetch('createdAt', 'DESC');
    }
  }, [onRefetch, mapColumnKeyToBackendField]);

  const columns: EditableColumn<ProposedTopicRow>[] = [
    {
      key: 'category',
      header: 'Category',
      editable: false,
      accessor: 'category',
      enableSorting: true,
      size: 140,
      minSize: 120,
      maxSize: 180,
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0 w-7 h-7 rounded border border-gray-200 bg-gray-50 flex items-center justify-center">
            <FolderOpen className="w-3.5 h-3.5 text-gray-600" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-gray-900 text-sm truncate">
              {row.category}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {row.topics?.length || 0} {row.topics?.length === 1 ? 'topic' : 'topics'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'topics',
      header: 'Topics',
      editable: false,
      enableSorting: true,
      size: 600,
      minSize: 400,
      grow: true,
      render: (row) => (
        <div className="space-y-2 py-1">
          {row.topics && row.topics.length > 0 ? (
            row.topics.map((topicItem, index) => {
              const topic = typeof topicItem === 'string' ? topicItem : topicItem.topic;
              const url = typeof topicItem === 'string' ? undefined : topicItem.url;
              const date = typeof topicItem === 'string' ? undefined : topicItem.date;
              const industryScores = typeof topicItem === 'string' ? undefined : topicItem.industryScores;
              const relevanceScore = typeof topicItem === 'string' ? undefined : topicItem.relevanceScore;
              const topicKey = `${row.category}-${index}`;
              const isExpanded = expandedTopics.has(topicKey);
              const isLongText = topic.length > MAX_TEXT_LENGTH;
              const displayText = isLongText && !isExpanded
                ? `${topic.substring(0, MAX_TEXT_LENGTH)}...`
                : topic;

              // Format date for display
              const formattedDate = date
                ? new Date(date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
                : null;

              return (
                <div
                  key={index}
                  className="group flex items-start gap-2.5 py-2 px-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <FileText className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 leading-relaxed">
                          {displayText}
                          {isLongText && (
                            <button
                              onClick={() => toggleTopicExpansion(topicKey)}
                              className={`ml-1 text-xs text-blue-600 hover:text-blue-700 font-medium transition-opacity cursor-pointer ${isExpanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                }`}
                            >
                              {isExpanded ? ' Less' : ' More'}
                            </button>
                          )}
                        </p>
                        {/* Metadata row - date and relevance */}
                        {(formattedDate || (industryScores && industryScores.length > 0)) && (
                          <div className="flex items-center gap-3 mt-1">
                            {formattedDate && (
                              <span className="text-xs text-gray-500">
                                Published: {formattedDate}
                              </span>
                            )}
                            {industryScores && industryScores.length > 0 && (
                              <RelevanceScoreDisplay relevanceScore={relevanceScore} industryScores={industryScores} />
                            )}
                          </div>
                        )}
                      </div>
                      {url && (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 mt-0.5 text-blue-600 hover:text-blue-700 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="py-6 text-sm text-gray-500 text-center">
              No topics available
            </div>
          )}
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Proposed Topics
          </h2>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <span className="text-sm text-red-700">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Proposed Topics
            </h2>
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          </div>
          {rows.length > 0 && (
            <div className="hidden sm:block text-sm text-gray-500">
              {rows.reduce((acc, row) => acc + (row.topics?.length || 0), 0)} topics
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <EditableDataTable<ProposedTopicRow>
          title="Proposed Topics"
          columns={columns}
          data={rows}
          enableEditing={false}
          enableDelete={false}
          keyExtractor={(row, idx) => `category-${row.category}-${idx}`}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          loading={loading}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting={!!onRefetch}
          onGlobalSearch={onGlobalSearch}
          globalFilterValue={globalFilterValue}
        />
      </div>

      {/* Empty State */}
      {!loading && rows.length === 0 && (
        <div className="bg-white border border-gray-200 rounded p-8">
          <div className="text-center">
            <p className="text-sm text-gray-500">
              No topics available
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================
 * OLD IMPLEMENTATION - COMMENTED FOR FUTURE REFERENCE
 * ============================================
 * 
 * This was the previous implementation that showed topics with engagement estimates,
 * content type, and suggested templates. It used EditableDataTable for editing capabilities.
 * 
 * If you need to revert to this implementation, uncomment the code below and update
 * the component props and imports accordingly.
 * 
 * interface TopicsTabProps {
 *   description: string;
 *   rows: TopicRow[];
 *   loading?: boolean;
 *   error?: string | null;
 * }
 * 
 * export const TopicsTab: FC<TopicsTabProps> = ({
 *   description,
 *   rows,
 *   loading = false,
 *   error = null,
 * }) => {
 *   const columns: EditableColumn<TopicRow>[] = [
 *     {
 *       key: 'topic',
 *       header: 'Topics',
 *       editable: true,
 *       validate: (value: string) => {
 *         if (!value?.trim()) return 'Topic is required';
 *         return undefined;
 *       },
 *     },
 *     {
 *       key: 'clicks',
 *       header: 'Estimated Engagement',
 *       editable: true,
 *       validate: (value: number) => {
 *         if (!value || value < 0) return 'Clicks must be a positive number';
 *         return undefined;
 *       },
 *     },
 *     {
 *       key: 'type',
 *       header: 'Type',
 *       editable: true,
 *     },
 *     {
 *       key: 'template',
 *       header: 'Suggested Template',
 *       editable: true,
 *       validate: (value: string) => {
 *         if (!value?.trim()) return 'Template is required';
 *         return undefined;
 *       },
 *     },
 *   ];
 * 
 *   if (loading) {
 *     return (
 *       <div className="space-y-4">
 *         <div>
 *           <h2 className="text-xl font-semibold text-gray-900">
 *             Proposed Topics & Engagement Estimates
 *           </h2>
 *           <p className="text-sm text-gray-600 mt-1">{description}</p>
 *         </div>
 *         <div className="animate-pulse space-y-3">
 *           {[1, 2, 3, 4, 5].map((i) => (
 *             <div key={i} className="h-12 bg-gray-200 rounded"></div>
 *           ))}
 *         </div>
 *       </div>
 *     );
 *   }
 * 
 *   if (error) {
 *     return (
 *       <div className="space-y-4">
 *         <div>
 *           <h2 className="text-xl font-semibold text-gray-900">
 *             Proposed Topics & Engagement Estimates
 *           </h2>
 *           <p className="text-sm text-gray-600 mt-1">{description}</p>
 *         </div>
 *         <div className="text-red-600">{error}</div>
 *       </div>
 *     );
 *   }
 * 
 *   return (
 *     <div className="space-y-4">
 *       <div>
 *         <h2 className="text-xl font-semibold text-gray-900">
 *           Proposed Topics & Engagement Estimates
 *         </h2>
 *         <p className="text-sm text-gray-600 mt-1">{description}</p>
 *       </div>
 * 
 *       {rows.length === 0 ? (
 *         <div className="text-gray-500 text-center py-8">
 *           No topics available.
 *         </div>
 *       ) : (
 *         <EditableDataTable
 *           title="Proposed Topics & Engagement Estimates"
 *           columns={columns}
 *           data={rows}
 *           keyExtractor={(_, idx) => `topic-${idx}`}
 *           enableEditing={true}
 *           enableDelete={true}
 *           initialEditingMode="cell"
 *         />
 *       )}
 *     </div>
 *   );
 * };
 */
